import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import EtBtnFormBasic from '@/components/common/buttons/EtBtnFormBasic';
import EtContentEditorLight2 from '@/utils/EtContentEditorLight2';
import PulseLoader from 'react-spinners/PulseLoader';
import { useGetUserMissionGoalsQuery, useUpdateUserMissionGoalMutation } from '@/features/userMissionGoals/userMissionGoalsApiSlice';
import { useGetGoalTypesQuery } from '@/features/goalTypes/goalTypesApiSlice'
import { produce } from 'immer';
import moment from 'moment'; // 22 Sep 2025

const EtUpdateUserMissionGoalComp = ({
  id,
  userId = "",
  userOrgId = "",
  missionId = "",
  missionModel = "",
  triggeringUrl = "",
  onClose,
  refetchOnSuccess,
  missionName,
}) => {
  // First Created - 18 Aug 2025
  // Author - Atul Pandey
  // Purpose - To update a UserMissionGoal with nested breakdowns and notes
  // Updated - 12 Sep 2025 - added goalUoM
  // Updated - 22 Sep 2025 - fixed a few issues - parseFloat for numeber fields, date fields display by splitting the Time part of the incoming date data

  // 0 - Set Navigate
  const navigate = useNavigate();

  // 1 - Local state for loading
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [editorKey, setEditorKey] = useState('goalDesc-');

  // 2 - RTK Query & Mutations for data fetching and updates

  const [updateUserMissionGoal, {
      isLoading,
      isSuccess,
      isError,
      error,
    }] = useUpdateUserMissionGoalMutation()

  const { data: userMissionGoalData, isLoading: isLoadingGoals } = useGetUserMissionGoalsQuery();
  const { data: goalTypesData} = useGetGoalTypesQuery()

  const extractEntitiesFromAdapter = (dataAdapter) => {
    if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
    return dataAdapter.ids.map((id) => dataAdapter.entities[id]);
  };

  const allGoals = extractEntitiesFromAdapter(userMissionGoalData);
  const currentGoal = allGoals.find((goal) => goal._id === id);
  const goalTypes = extractEntitiesFromAdapter(goalTypesData)


  // console.log('all Goals', allGoals)
  // console.log('currentGoal', currentGoal)
  //   console.log('goalTypes', goalTypes)
  

  // 3a - Local form state
  const [formData, setFormData] = useState({
    goalName: '',
    goalDesc: '',
    goalType: '',
    goalTargetValue: null,
    goalUoM: '', // 12 Sep 2025
    goalBreakdown: [],
    goalNotes: [],
    active: true,
    deletedGoalBreakdowns: [],   // ✅ 18 Aug 2025 - Added 
  });

  // 3b Initialize form only once when data is ready
  useEffect(() => {
    if (currentGoal && !isFormInitialized) {
      const isShared = !!currentGoal.sharedGoalId;

      setFormData({
        goalName: isShared ? currentGoal.sharedGoalId?.goalName : currentGoal?.goalName,
        goalDesc: isShared ? currentGoal.sharedGoalId?.goalDesc : currentGoal?.goalDesc,
        goalType: isShared ? currentGoal.sharedGoalId.goalType?._id || currentGoal.sharedGoalId.goalType : currentGoal.goalType?._id || currentGoal.goalType ,
        goalTargetValue: isShared ? currentGoal.sharedGoalId.goalTargetValue : currentGoal.goalTargetValue,
        goalUoM: isShared ? currentGoal.sharedGoalId.goalUoM : currentGoal.goalUoM, // 12 Sep 2025
        goalBreakdown: isShared ? currentGoal.sharedGoalId.goalBreakdown : currentGoal.goalBreakdown || [],
        // goalNotes: isShared ? currentGoal.sharedGoalId.goalNotes : currentGoal.goalNotes || [],
        goalNotes: currentGoal.goalNotes || [],
        // active: isShared
        //   ? currentGoal.sharedGoalId.active ?? true
        //   : currentGoal.active ?? true,
        active: true
      });

      setIsFormInitialized(true);
    }
  }, [currentGoal, isFormInitialized]);

  // 3c - 22 Sep 2025 - for auto populating the goal period dates based on periodType
  const getPeriodRangeFromType = (periodType, fromDate = moment()) => {
    const start = moment(fromDate);
    let periodStart = '';
    let periodEnd = '';

    if (periodType === "Week") {
      periodStart = start.clone().startOf('isoWeek').format('YYYY-MM-DD');
      periodEnd = start.clone().endOf('isoWeek').format('YYYY-MM-DD');
    } else if (periodType === "Month") {
      periodStart = start.clone().startOf('month').format('YYYY-MM-DD');
      periodEnd = start.clone().endOf('month').format('YYYY-MM-DD');
    } else if (periodType === "Quarter") {
      periodStart = start.clone().startOf('quarter').format('YYYY-MM-DD');
      periodEnd = start.clone().endOf('quarter').format('YYYY-MM-DD');
    }

    return { periodStart, periodEnd };
  };



  // 4 - Handle Submit
  const handleSubmit = async () => {

    // 22 Sep 2025 - Validate breakdown date ranges
    const invalidBreakdown = formData.goalBreakdown.find((b) => {
      return b.periodStart && b.periodEnd && new Date(b.periodEnd) < new Date(b.periodStart);
    });

    if (invalidBreakdown) {
      alert("At least one goal breakdown has periodEnd before periodStart.");
      return;
    }

    try {
      const payload = {
        goalId: id, // ✅ backend expects `goalId`
        userId,
        userOrgId,
        missionId,
        missionModel,
        goalName: formData.goalName?.trim() || undefined,
        goalDesc: formData.goalDesc?.trim() || undefined,
        goalType: formData.goalType || undefined,
        goalTargetValue: formData.goalTargetValue || undefined,
        goalUoM: formData.goalUoM || "", // 12 Sep 2025
        goalBreakdown: [
          ...(formData.goalBreakdown || []).map((item) => ({
            _id: item._id || undefined,
            period: item.period?.trim() || undefined,
            periodType: item.periodType || undefined,
            periodStart: item.periodStart || undefined,
            periodEnd: item.periodEnd || undefined,
            targetValue: item.targetValue || undefined,
            actualValue: item.actualValue || undefined,
            notes: item.notes?.trim() || undefined,
            updatedAt: new Date(),
          })),
          ...(formData.deletedGoalBreakdowns || []),
        ],
        goalNotes: [
          ...(formData.goalNotes || []).map((note) => ({
            _id: note._id || undefined,
            noteText: note.noteText?.trim() || undefined,
            noteDate: note.noteDate || new Date(),
            noteUpdateDate: note.noteUpdateDate || new Date(),
            sharedWithAll: note.sharedWithAll || false,
          })),
          ...(formData.deletedGoalNotes || []),
        ],
        active: formData.active,
      };

      const result = await updateUserMissionGoal(payload).unwrap();

      if (refetchOnSuccess) refetchOnSuccess();
      if (onClose) onClose();
      else navigate(triggeringUrl || '/etapp');

    } catch (error) {
      console.error("❌ Error updating goal:", error);
    }
  };

  // 5 - Handle Clear
  const handleClear = () => {
    setFormData({
      goalName: '',
      goalDesc: '',
      goalType: '',
      goalTargetValue: null,
      goalUoM: '', // 12 Sep 2025
      goalBreakdown: [],
      goalNotes: [],
      active: true,
      deletedGoalBreakdowns: [],   // ✅ 18 Aug 2025 - Added 
    });
  };

  // 6 - Handle Cancel
  const handleCancel = () => {
    if (onClose) onClose();
    else navigate(triggeringUrl || '/etapp');
  };


  // 7 - Temporary loader / placeholder
  if (isLoadingGoals || !isFormInitialized) {
    return (
      <div className="p-4">
        <PulseLoader color={'#ea580c'} />
        <p className="text-xs mt-2 text-orange-600">Loading goal data...</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full m-1 p-1 bg-white rounded shadow-md shadow-black">
      <p className="p-1 m-1 font-bold font-poppins text-xl">
        Update User Mission Goal
      </p>

      <div className='m-1 p-1 px-2 mx-2 flex justify-start items-center bg-orange-100 rounded'>
        <p className="m-1 p-1 block text-gray-600 text-sm font-bold mb-1">
            Mission Name : </p>
        <p className='italic'>{missionName}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 mt-4 m-1 p-1 text-xs font-poppins">
        {/* 1 - Goal Name */}
        <div className="grid grid-cols-12 items-center gap-2 m-2 p-2 bg-gray-50 rounded shadow-sm">
          <label className="col-span-2 text-xs font-bold text-gray-700">Goal Name</label>
          <input
            type="text"
            className="col-span-10 border border-gray-300 rounded p-1 text-sm"
            value={formData.goalName}
            onChange={(e) =>
              setFormData(produce(draft => { draft.goalName = e.target.value }))
            }
          />
        </div>

        {/* 2-  Goal Description */}
        <div className="grid grid-cols-12 items-center gap-2 m-2 p-2 bg-gray-50 rounded shadow-sm">
          <label className="col-span-2 text-xs font-bold text-gray-700">Goal Description</label>
          <div className="col-span-10">
            <EtContentEditorLight2
              value={formData.goalDesc}
              handleChange={(value) =>
                setFormData(produce((draft) => {
                  draft.goalDesc = value;
                }))
              }
              placeholderText="Write the goal description here!"
              editorKey={editorKey}
            />
          </div>
        </div>

        {/* 3 - Goal Type */}
        <div className="grid grid-cols-12 items-center gap-2 m-2 p-2 bg-gray-50 rounded shadow-sm">
          <label className="col-span-2 text-xs font-bold text-gray-700">Goal Type</label>
          <select
            className="col-span-10 border border-gray-300 rounded p-1 text-sm"
            value={formData.goalType}
            onChange={(e) =>
              setFormData(produce(draft => { draft.goalType = e.target.value }))
            }
          >
            <option value="">Select Goal Type</option>
            {goalTypes?.map((type) => (
              <option key={type._id} value={type._id}>
                {type.goalTypeName}
              </option>
            ))}
          </select>
        </div>

        {/* 4a - Target Value */}
        <div className="grid grid-cols-12 items-center gap-2 m-2 p-2 bg-gray-50 rounded shadow-sm">
          <label className="col-span-2 text-xs font-bold text-gray-700">Target Value</label>
          <input
            type="number"
            className="col-span-10 border border-gray-300 rounded p-1 text-sm"
            value={formData.goalTargetValue}
            onChange={(e) =>
              setFormData(produce(draft => {
                draft.goalTargetValue = parseFloat(e.target.value);
              }))
            }
          />
        </div>

        {/* 4b - 12 Sep 2025 - Goal UoM */}
        <div className="grid grid-cols-12 items-center gap-2 m-2 p-2 bg-gray-50 rounded shadow-sm">
          <label className="col-span-2 text-xs font-bold text-gray-700">Goal UoM</label>
          <input
            type="text"
            className="col-span-10 border border-gray-300 rounded p-1 text-sm"
            value={formData.goalUoM}
            onChange={(e) =>
              setFormData(produce(draft => { draft.goalUoM = e.target.value }))
            }
          />
        </div>

        {/* 5 - Goal Breakdown Section */}
        <div className="border rounded p-2 m-2 bg-gray-50">
          <div className="flex justify-between items-center mb-2">
            <p className="font-bold text-sm">Goal Breakdown</p>
            <button
              type="button"
              className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
              onClick={() =>
                setFormData((prevState) =>
                  produce(prevState, (draft) => {
                    draft.goalBreakdown.push({
                      period: '',
                      periodType: '',
                      periodStart: '',
                      periodEnd: '',
                      targetValue: '',
                      actualValue: '',
                      notes: '',
                    });
                  })
                )
              }
            >
              <PlusIcon className="h-4 w-4 inline" />
            </button>
          </div>

          {formData.goalBreakdown.map((item, index) => (
            <div key={item?._id+index} className="p-2 mb-2 border rounded bg-white">
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs font-bold">Breakdown {index + 1}</p>
                <button
                  type="button"
                  className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold p-1 rounded"
                  onClick={() =>
                    setFormData((prevState) =>
                      produce(prevState, (draft) => {
                        // Collect deleted item if it has _id
                        const removed = draft.goalBreakdown.splice(index, 1)[0];
                        if (removed._id) {
                          draft.deletedGoalBreakdowns = draft.deletedGoalBreakdowns || [];
                          draft.deletedGoalBreakdowns.push({ ...removed, delete: true });
                        }
                      })
                    )
                  }
                >
                  <MinusIcon className="h-4 w-4 inline" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <input
                  type="text"
                  placeholder="Period (e.g., 2025-01)"
                  value={item.period}
                  onChange={(e) =>
                    setFormData((prev) =>
                      produce(prev, (draft) => {
                        draft.goalBreakdown[index].period = e.target.value;
                      })
                    )
                  }
                  
                  className="p-1 border rounded"
                />
                <select
                  value={item.periodType}
                  // onChange={(e) =>
                  //   setFormData((prev) =>
                  //     produce(prev, (draft) => {
                  //       draft.goalBreakdown[index].periodType = e.target.value;
                  //     })
                  //   )
                  // }
                  onChange={(e) => {
                    const selectedType = e.target.value;
                    const { periodStart, periodEnd } = getPeriodRangeFromType(selectedType);

                    setFormData((prev) =>
                      produce(prev, (draft) => {
                        draft.goalBreakdown[index].periodType = selectedType;
                        draft.goalBreakdown[index].periodStart = periodStart;
                        draft.goalBreakdown[index].periodEnd = periodEnd;
                      })
                    );
                  }}
                  className="p-1 border rounded"
                >
                  <option value="">-- Period Type --</option>
                  <option value="Month">Month</option>
                  <option value="Quarter">Quarter</option>
                  <option value="Week">Week</option>
                </select>
                <input
                  type="date"
                  // value={item.periodStart} // 22 Sep 2025
                  value={item.periodStart ? item.periodStart.split('T')[0] : ''} // 22 Sep 2025
                  // onChange={(e) =>
                  //   setFormData((prev) =>
                  //     produce(prev, (draft) => {
                  //       draft.goalBreakdown[index].periodStart = e.target.value;
                  //     })
                  //   )
                  // }
                  onChange={(e) => {
                    const newStart = e.target.value;
                    const periodType = formData.goalBreakdown[index].periodType;
                    
                    const { periodEnd } = getPeriodRangeFromType(periodType, moment(newStart));

                    setFormData((prev) =>
                      produce(prev, (draft) => {
                        draft.goalBreakdown[index].periodStart = newStart;
                        draft.goalBreakdown[index].periodEnd = periodEnd;
                      })
                    );
                  }}

                  className="p-1 border rounded"
                />
                <input
                  type="date"
                  // value={item.periodEnd} // 22 Sep 2025
                   value={item.periodEnd ? item.periodEnd.split('T')[0] : ''} // 22 Sep 2025
                  // onChange={(e) =>
                  //   setFormData((prev) =>
                  //     produce(prev, (draft) => {
                  //       draft.goalBreakdown[index].periodEnd = e.target.value;
                  //     })
                  //   )
                  // }
                  onChange={(e) => {
                    const newEnd = e.target.value;
                    const currentStart = formData.goalBreakdown[index].periodStart;

                    if (currentStart && new Date(newEnd) < new Date(currentStart)) {
                      alert("Period end date cannot be before start date.");
                      return;
                    }

                    setFormData((prev) =>
                      produce(prev, (draft) => {
                        draft.goalBreakdown[index].periodEnd = newEnd;
                      })
                    );
                  }}
                  className="p-1 border rounded"
                />
                <input
                  type="number"
                  placeholder="Target"
                  value={item.targetValue}
                  onChange={(e) =>
                    setFormData((prev) =>
                      produce(prev, (draft) => {
                        // draft.goalBreakdown[index].targetValue = e.target.value; // 22 Sep 2025
                        draft.goalBreakdown[index].targetValue = parseFloat(e.target.value) || 0; // 22 Sep 2025
                      })
                    )
                  }
                  className="p-1 border rounded"
                />
                <input
                  type="number"
                  placeholder="Actual"
                  value={item.actualValue}
                  // readOnly // ✅ 22 Sep 2025 Now read-only
                  disabled // ✅ 22 Sep 2025 Now read-only
                  onChange={(e) =>
                    setFormData((prev) =>
                      produce(prev, (draft) => {
                        // draft.goalBreakdown[index].actualValue = e.target.value; // 22 Sep 2025
                        draft.goalBreakdown[index].actualValue = parseFloat(e.target.value) || 0; // 22 Sep 2025
                      })
                    )
                  }
                  className="p-1 border rounded bg-orange-100 text-gray-700 cursor-default"
                />
              </div>

              <textarea
                placeholder="Breakdown Notes"
                value={item.notes}
                onChange={(e) =>
                  setFormData((prev) =>
                    produce(prev, (draft) => {
                      draft.goalBreakdown[index].notes = e.target.value;
                    })
                  )
                }
                className="mt-2 w-full p-1 border rounded"
              />
            </div>
          ))}
        </div>

        {/* 6️⃣ Goal Notes Section */}
        <div className="border rounded p-2 m-2 bg-gray-50">
          <div className="flex justify-between items-center mb-2">
            <p className="font-bold text-sm">Goal Notes</p>
            <button
              type="button"
              className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
              onClick={() =>
                setFormData((prev) =>
                  produce(prev, (draft) => {
                    draft.goalNotes.push({
                      noteText: '',
                      noteDate: new Date(),
                      noteUpdateDate: new Date(),
                      sharedWithAll: false,
                    });
                  })
                )
              }
            >
              <PlusIcon className="h-4 w-4 inline" />
            </button>
          </div>

          {formData.goalNotes.map((note, index) => (
            <div key={`goal-note-${index}`} className="p-2 mb-2 border rounded bg-white">
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs font-bold">Note {index + 1}</p>
                <div className="flex justify-start items-center">
                  <p className="font-bold text-xs mx-1 px-1">Remove</p>
                  <button
                    type="button"
                    className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold p-1 rounded"
                    onClick={() =>
                      setFormData((prev) =>
                        produce(prev, (draft) => {
                          const removed = draft.goalNotes.splice(index, 1)[0];
                          if (removed._id) {
                            draft.deletedGoalNotes = draft.deletedGoalNotes || [];
                            draft.deletedGoalNotes.push({ ...removed, delete: true });
                          }
                        })
                      )
                    }
                  >
                    <MinusIcon className="h-4 w-4 inline" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-12 items-start gap-2 mb-2">
                <label className="col-span-2 text-xs font-bold text-gray-700 mt-2">Note Text</label>
                <div className="col-span-10">
                  <EtContentEditorLight2
                    value={note.noteText}
                    handleChange={(value) =>
                      setFormData((prev) =>
                        produce(prev, (draft) => {
                          draft.goalNotes[index].noteText = value;
                          draft.goalNotes[index].noteUpdateDate = new Date();
                        })
                      )
                    }
                    placeholderText="Write your note here..."
                    editorKey={`goal-note-${index}`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-12 gap-2 items-center">
                <label className="col-span-2 text-xs font-bold text-gray-700">Note Date</label>
                <input
                  type="date"
                  // value={note?.noteDate?.split('T')[0] || ''}
                  value={note?.noteDate ? new Date(note.noteDate).toISOString().split('T')[0] : ''}
                  onChange={(e) =>
                    setFormData((prev) =>
                      produce(prev, (draft) => {
                        draft.goalNotes[index].noteDate = e.target.value;
                      })
                    )
                  }
                  className="col-span-4 border border-gray-300 rounded p-1 text-sm"
                />

                <label className="col-span-2 text-xs font-bold text-gray-700">Shared With All</label>
                <input
                  type="checkbox"
                  checked={note.sharedWithAll || false}
                  onChange={(e) =>
                    setFormData((prev) =>
                      produce(prev, (draft) => {
                        draft.goalNotes[index].sharedWithAll = e.target.checked;
                      })
                    )
                  }
                  className="col-span-1"
                />
              </div>
            </div>
          ))}
        </div>
 
        {/* 7 - Action Buttons */}
        <div className="bg-white m-1 px-1 py-3 rounded">
          <EtBtnFormBasic name="Update" type="create" handleClick={handleSubmit} />
          <EtBtnFormBasic name="Clear" type="clear" handleClick={handleClear} />
          <EtBtnFormBasic name="Cancel" type="cancel" handleClick={handleCancel} />
        </div>

      </form>

    </div>
  );
};

export default EtUpdateUserMissionGoalComp;